import { getSupabaseAuthContext, supabase } from "@/lib/supabase"
import { hasExplicitProfileFullName, resolveCanonicalProfileAvatar, resolveCanonicalProfileName } from "./profileIdentity"

export type VillageHubCreatorProfile = {
  id: string
  username?: string | null
  email?: string | null
  name: string
  avatarUrl: string | null
  bio?: string | null
  accountType?: string | null
  accountCreatedAt?: string | null
  isVerified: boolean
  hasExplicitFullName?: boolean
  profileDistrict: string | null
  profileBlock: string | null
  profileGramPanchayatId: string | null
  profileGramPanchayatName: string | null
}

export type CreatorFollowSummary = {
  creatorId: string
  followerCount: number
  followingCount: number
  isFollowing: boolean
}

export type CreatorProfileListItem = {
  profile: VillageHubCreatorProfile
  relationCreatedAt: string | null
  isFollowedByViewer: boolean
  followerCount: number
  followingCount: number
}

export type CreatorProfileListPage = {
  items: CreatorProfileListItem[]
  page: {
    limit: number
    returnedCount: number
    nextCursor: string | null
    hasMore: boolean
  }
}

export type AppNotificationItem = {
  id: string
  type: string
  createdAt: string
  readAt: string | null
  actorId: string | null
  actorName: string
  actorAvatarUrl: string | null
  entityId: string | null
}

type CreatorProfileRow = {
  id?: string | null
  username?: string | null
  email?: string | null
  full_name?: string | null
  fullName?: string | null
  display_name?: string | null
  displayName?: string | null
  avatar_url?: string | null
  avatarUrl?: string | null
  profile_photo?: string | null
  profilePhoto?: string | null
  bio?: string | null
  account_type?: string | null
  accountType?: string | null
  account_created_at?: string | null
  accountCreatedAt?: string | null
  member_verified?: boolean | null
  isVerified?: boolean | null
  district?: string | null
  block?: string | null
  village?: string | null
  gram_panchayat_id?: string | null
  gramPanchayatId?: string | null
  gram_panchayat_name?: string | null
  gramPanchayatName?: string | null
}

type FollowSummaryRow = {
  profileId?: string | null
  is_following?: boolean | null
  follower_count?: number | null
  following_count?: number | null
}

const NOTIFICATION_COUNT_STORAGE_KEY = "app.notifications.count"
const PROFILE_FOLLOWS_FUNCTION_URL = `${String(import.meta.env.VITE_SUPABASE_URL || "").trim().replace(/\/+$/, "")}/functions/v1/profile-follows`
const viewerFollowStateCache = new Map<string, Map<string, boolean>>()
const viewerFollowStateInflight = new Map<string, Promise<Record<string, boolean>>>()

function normalizeId(value: unknown) {
  return String(value || "").trim()
}

function normalizeCount(value: unknown) {
  const count = Number(value || 0)
  if (!Number.isFinite(count) || count < 0) return 0
  return Math.floor(count)
}

function getViewerFollowStateCache(viewerUserId: string, create = false) {
  const normalizedViewerUserId = normalizeId(viewerUserId)
  if (!normalizedViewerUserId) return null
  let cache = viewerFollowStateCache.get(normalizedViewerUserId) || null
  if (!cache && create) {
    cache = new Map<string, boolean>()
    viewerFollowStateCache.set(normalizedViewerUserId, cache)
  }
  return cache
}

function createViewerFollowInflightKey(viewerUserId: string, creatorIds: string[]) {
  return `${normalizeId(viewerUserId)}::${creatorIds.slice().sort().join(",")}`
}

function mapProfileRow(row: CreatorProfileRow | null | undefined): VillageHubCreatorProfile | null {
  const id = normalizeId(row?.id)
  if (!id) return null
  return {
    id,
    username: String(row?.username || "").trim() || null,
    email: String(row?.email || "").trim() || null,
    name: resolveCanonicalProfileName({
      full_name: row?.full_name,
      fullName: row?.fullName,
      display_name: row?.display_name,
      displayName: row?.displayName,
      username: row?.username,
      email: row?.email,
      defaultName: "Creator",
    }),
    avatarUrl: resolveCanonicalProfileAvatar({
      profile_photo: row?.profile_photo,
      profilePhoto: row?.profilePhoto,
      avatar_url: row?.avatar_url,
      avatarUrl: row?.avatarUrl,
    }),
    bio: String(row?.bio || "").trim() || null,
    accountType: String((row?.account_type ?? row?.accountType) || "").trim() || null,
    accountCreatedAt: String((row?.account_created_at ?? row?.accountCreatedAt) || "").trim() || null,
    isVerified: Boolean(row?.member_verified ?? row?.isVerified),
    hasExplicitFullName: hasExplicitProfileFullName(row || {}),
    profileDistrict: String(row?.district || "").trim() || null,
    profileBlock: String(row?.block || "").trim() || null,
    profileGramPanchayatId: String((row?.gram_panchayat_id ?? row?.gramPanchayatId) || "").trim() || null,
    profileGramPanchayatName: String((row?.gram_panchayat_name ?? row?.gramPanchayatName ?? row?.village) || "").trim() || null,
  }
}

function mapFollowSummaryRow(row: FollowSummaryRow | null | undefined, creatorId: string): CreatorFollowSummary {
  return {
    creatorId,
    followerCount: normalizeCount((row as any)?.follower_count ?? (row as any)?.followerCount),
    followingCount: normalizeCount((row as any)?.following_count ?? (row as any)?.followingCount),
    isFollowing: Boolean((row as any)?.is_following ?? (row as any)?.isFollowing),
  }
}

function makeFunctionUrl(query?: Record<string, string | number | null | undefined>) {
  const url = new URL(PROFILE_FOLLOWS_FUNCTION_URL)
  for (const [key, value] of Object.entries(query || {})) {
    if (value == null || String(value).trim() === "") continue
    url.searchParams.set(key, String(value))
  }
  return url.toString()
}

async function invokeProfileFollowsApi<T>(args: {
  method?: "GET" | "POST"
  query?: Record<string, string | number | null | undefined>
  body?: Record<string, unknown>
  authRequired?: boolean
}): Promise<T> {
  const method = args.method || "GET"
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  }
  let authContext: Awaited<ReturnType<typeof getSupabaseAuthContext>> | null = null

  if (args.authRequired) {
    authContext = await getSupabaseAuthContext()
    headers.Authorization = `Bearer ${authContext.accessToken}`
  } else {
    const session = await supabase.auth.getSession().then((r) => r.data.session).catch(() => null)
    if (session?.access_token) {
      headers.Authorization = `Bearer ${session.access_token}`
    }
  }

  const url = makeFunctionUrl(args.query)

  const res = await fetch(url, {
    method,
    headers,
    body: method === "POST" ? JSON.stringify(args.body || {}) : undefined,
  })

  const payload = await res.json().catch(() => null)
  if (!res.ok) {
    const err: any = new Error(String((payload as any)?.error || res.statusText || "REQUEST_FAILED"))
    err.status = res.status
    err.payload = payload
    throw err
  }
  return payload as T
}

export function normalizeCreatorFollowError(error: unknown) {
  const code = String((error as any)?.code || (error as any)?.status || "").trim().toUpperCase()
  const message = String((error as any)?.message || (error as any)?.payload?.error || "").trim().toUpperCase()
  const merged = `${code} ${message}`
  if (merged.includes("UNAUTHENTICATED")) return "UNAUTHENTICATED"
  if (merged.includes("CANNOT_FOLLOW_SELF")) return "CANNOT_FOLLOW_SELF"
  if (merged.includes("CANNOT_UNFOLLOW_SELF")) return "CANNOT_UNFOLLOW_SELF"
  if (merged.includes("PROFILE_NOT_FOUND")) return "PROFILE_NOT_FOUND"
  if (merged.includes("PROFILE_REQUIRED")) return "PROFILE_REQUIRED"
  if (merged.includes("RATE_LIMITED") || code === "429") return "RATE_LIMITED"
  return "UNKNOWN"
}

export async function fetchVillageHubCreatorProfile(creatorId: string) {
  const id = normalizeId(creatorId)
  if (!id) return null
  const res = await invokeProfileFollowsApi<{ profile: CreatorProfileRow | null }>({
    method: "GET",
    query: { scope: "summary", profile_id: id },
  })
  return mapProfileRow(res.profile)
}

function buildProfileSeedFromAuthUser(userLike: any) {
  const id = normalizeId(userLike?.id)
  if (!id) return null

  const email = String(userLike?.email || userLike?.user_metadata?.email || "").trim() || null
  const username =
    String(
      userLike?.user_metadata?.username
      || userLike?.user_metadata?.preferred_username
      || userLike?.user_metadata?.user_name
      || (email ? email.split("@")[0] : "")
      || ""
    ).trim() || null
  const avatarUrl =
    String(userLike?.user_metadata?.avatar_url || userLike?.user_metadata?.picture || "").trim() || null
  const fullName = resolveCanonicalProfileName({
    full_name: userLike?.user_metadata?.full_name,
    fullName: userLike?.user_metadata?.name,
    display_name: userLike?.user_metadata?.display_name,
    username,
    email,
    defaultName: "",
  })

  const payload: Record<string, unknown> = { id }
  if (fullName) payload.full_name = fullName
  if (username) payload.username = username
  if (email) payload.email = email
  if (avatarUrl) {
    payload.avatar_url = avatarUrl
    payload.profile_photo = avatarUrl
  }
  return payload
}

export async function ensureAuthenticatedProfileRecord(userLike?: any) {
  const user = userLike || (await supabase.auth.getUser().then((res) => res.data.user).catch(() => null))
  const payload = buildProfileSeedFromAuthUser(user)
  if (!payload?.id) return
  await supabase.from("profiles").upsert(payload, { onConflict: "id" })
}

export async function syncOwnProfileFromCreatorData(args: {
  currentUser?: any
  profileId: string
  authorName?: string | null
  authorAvatarUrl?: string | null
}) {
  const currentUser = args.currentUser || (await supabase.auth.getUser().then((res) => res.data.user).catch(() => null))
  const currentUserId = normalizeId(currentUser?.id)
  const profileId = normalizeId(args.profileId)
  if (!currentUserId || !profileId || currentUserId !== profileId) return

  const authorName = resolveCanonicalProfileName({
    full_name: args.authorName,
    defaultName: "",
  })
  const authorAvatarUrl = String(args.authorAvatarUrl || "").trim() || null

  const payload: Record<string, unknown> = { id: profileId }
  if (authorName) payload.full_name = authorName
  if (authorAvatarUrl) {
    payload.avatar_url = authorAvatarUrl
    payload.profile_photo = authorAvatarUrl
  }
  if (Object.keys(payload).length <= 1) return
  await supabase.from("profiles").upsert(payload, { onConflict: "id" })
}

export async function fetchCreatorFollowSummary(args: { creatorId: string; viewerUserId?: string | null }): Promise<CreatorFollowSummary> {
  const creatorId = normalizeId(args.creatorId)
  if (!creatorId) {
    return { creatorId: "", followerCount: 0, followingCount: 0, isFollowing: false }
  }

  const res = await invokeProfileFollowsApi<{ follow: FollowSummaryRow }>({
    method: "GET",
    query: { scope: "summary", profile_id: creatorId },
  })
  return mapFollowSummaryRow(res.follow, creatorId)
}

export async function fetchViewerFollowStateMap(args: { viewerUserId?: string | null; creatorIds: string[] }) {
  const viewerUserId = normalizeId(args.viewerUserId)
  const creatorIds = Array.from(new Set((args.creatorIds || []).map((id) => normalizeId(id)).filter(Boolean)))
  const states = new Map<string, boolean>()
  if (!viewerUserId || !creatorIds.length) return states

  const cache = getViewerFollowStateCache(viewerUserId, true)
  const missingCreatorIds: string[] = []
  for (const creatorId of creatorIds) {
    if (cache?.has(creatorId)) {
      if (cache.get(creatorId)) states.set(creatorId, true)
      continue
    }
    missingCreatorIds.push(creatorId)
  }

  if (!missingCreatorIds.length) return states

  const inflightKey = createViewerFollowInflightKey(viewerUserId, missingCreatorIds)
  let inflightRequest = viewerFollowStateInflight.get(inflightKey)
  if (!inflightRequest) {
    inflightRequest = (async () => {
      const res = await invokeProfileFollowsApi<{ states: Record<string, boolean> }>({
        method: "GET",
        query: { scope: "states", ids: missingCreatorIds.join(",") },
        authRequired: true,
      })

      const fetchedStates: Record<string, boolean> = {}
      const cacheForViewer = getViewerFollowStateCache(viewerUserId, true)
      for (const creatorId of missingCreatorIds) {
        const isFollowing = Boolean(res.states?.[creatorId])
        fetchedStates[creatorId] = isFollowing
        cacheForViewer?.set(creatorId, isFollowing)
      }
      return fetchedStates
    })().finally(() => {
      viewerFollowStateInflight.delete(inflightKey)
    })
    viewerFollowStateInflight.set(inflightKey, inflightRequest)
  }

  const fetchedStates = await inflightRequest
  for (const [key, value] of Object.entries(fetchedStates || {})) {
    const id = normalizeId(key)
    if (id && value) states.set(id, true)
  }
  return states
}

export function updateViewerFollowStateCache(args: {
  viewerUserId?: string | null
  creatorId: string
  isFollowing: boolean
}) {
  const viewerUserId = normalizeId(args.viewerUserId)
  const creatorId = normalizeId(args.creatorId)
  if (!viewerUserId || !creatorId) return
  const cache = getViewerFollowStateCache(viewerUserId, true)
  cache?.set(creatorId, Boolean(args.isFollowing))
}

export function invalidateViewerFollowStateCache(args?: {
  viewerUserId?: string | null
  creatorId?: string | null
}) {
  const viewerUserId = normalizeId(args?.viewerUserId)
  const creatorId = normalizeId(args?.creatorId)
  if (!viewerUserId) {
    viewerFollowStateCache.clear()
    viewerFollowStateInflight.clear()
    return
  }
  if (!creatorId) {
    viewerFollowStateCache.delete(viewerUserId)
    for (const key of [...viewerFollowStateInflight.keys()]) {
      if (key.startsWith(`${viewerUserId}::`)) viewerFollowStateInflight.delete(key)
    }
    return
  }
  viewerFollowStateCache.get(viewerUserId)?.delete(creatorId)
}

export async function followCreator(creatorId: string) {
  const normalizedCreatorId = normalizeId(creatorId)
  const res = await invokeProfileFollowsApi<FollowSummaryRow>({
    method: "POST",
    body: { action: "follow", profileId: normalizedCreatorId },
    authRequired: true,
  })
  return mapFollowSummaryRow(res, normalizedCreatorId)
}

export async function unfollowCreator(creatorId: string) {
  const normalizedCreatorId = normalizeId(creatorId)
  const res = await invokeProfileFollowsApi<FollowSummaryRow>({
    method: "POST",
    body: { action: "unfollow", profileId: normalizedCreatorId },
    authRequired: true,
  })
  return mapFollowSummaryRow(res, normalizedCreatorId)
}

function mapListItem(row: any): CreatorProfileListItem {
  return {
    profile: mapProfileRow({
      id: row?.profile?.id,
      username: row?.profile?.username,
      email: row?.profile?.email,
      full_name: row?.profile?.fullName,
      fullName: row?.profile?.fullName,
      avatar_url: row?.profile?.avatarUrl,
      avatarUrl: row?.profile?.avatarUrl,
      bio: row?.profile?.bio,
      account_type: row?.profile?.accountType,
      account_created_at: row?.profile?.accountCreatedAt,
      member_verified: row?.profile?.isVerified,
      district: row?.profile?.district,
      block: row?.profile?.block,
      gram_panchayat_id: row?.profile?.gramPanchayatId,
      gram_panchayat_name: row?.profile?.gramPanchayatName,
    }) || {
      id: normalizeId(row?.profile?.id || row?.profileId || row?.profile_id),
      name: "Creator",
      avatarUrl: null,
      isVerified: false,
      profileDistrict: null,
      profileBlock: null,
      profileGramPanchayatId: null,
      profileGramPanchayatName: null,
    },
    relationCreatedAt: String(row?.relationCreatedAt || "").trim() || null,
    isFollowedByViewer: Boolean(row?.isFollowedByViewer),
    followerCount: normalizeCount(row?.followerCount),
    followingCount: normalizeCount(row?.followingCount),
  }
}

async function fetchCreatorListPage(query: Record<string, string | number | null | undefined>, authRequired = false): Promise<CreatorProfileListPage> {
  const res = await invokeProfileFollowsApi<any>({
    method: "GET",
    query,
    authRequired,
  })
  return {
    items: Array.isArray(res.items) ? res.items.map(mapListItem) : [],
    page: {
      limit: normalizeCount(res.page?.limit || 20) || 20,
      returnedCount: normalizeCount(res.page?.returnedCount || 0),
      nextCursor: String(res.page?.nextCursor || "").trim() || null,
      hasMore: Boolean(res.page?.hasMore),
    },
  }
}

export async function fetchCreatorFollowers(args: { creatorId: string; cursor?: string | null; limit?: number }) {
  return fetchCreatorListPage({
    scope: "followers",
    profile_id: normalizeId(args.creatorId),
    cursor: args.cursor || null,
    limit: args.limit ?? 20,
  })
}

export async function fetchCreatorFollowing(args: { creatorId: string; cursor?: string | null; limit?: number }) {
  return fetchCreatorListPage({
    scope: "following",
    profile_id: normalizeId(args.creatorId),
    cursor: args.cursor || null,
    limit: args.limit ?? 20,
  })
}

export async function fetchDiscoverCreators(args?: { cursor?: string | null; limit?: number }) {
  return fetchCreatorListPage({
    scope: "discover",
    cursor: args?.cursor || null,
    limit: args?.limit ?? 20,
  }, true)
}

export function writeStoredNotificationCount(count: number) {
  const safeCount = normalizeCount(count)
  try {
    localStorage.setItem(NOTIFICATION_COUNT_STORAGE_KEY, String(safeCount))
    window.dispatchEvent(new Event("app:notifications:update"))
  } catch {
    void 0
  }
}

export async function fetchUnreadNotificationCount(userId: string) {
  const normalizedUserId = normalizeId(userId)
  if (!normalizedUserId) return 0
  const res = await supabase
    .from("app_notifications")
    .select("id", { count: "exact", head: true })
    .eq("recipient_id", normalizedUserId)
    .is("read_at", null)

  if (res.error) throw res.error
  return normalizeCount(res.count)
}

export async function fetchRecentNotifications(userId: string): Promise<AppNotificationItem[]> {
  const normalizedUserId = normalizeId(userId)
  if (!normalizedUserId) return []
  const res = await supabase
    .from("app_notifications")
    .select("id, type, actor_id, entity_id, payload, read_at, created_at")
    .eq("recipient_id", normalizedUserId)
    .order("created_at", { ascending: false })
    .limit(20)

  if (res.error) throw res.error

  return ((res.data || []) as Array<any>).map((row) => {
    const payload = (row?.payload && typeof row.payload === "object") ? row.payload : {}
    return {
      id: normalizeId(row?.id),
      type: String(row?.type || "").trim() || "notification",
      createdAt: String(row?.created_at || ""),
      readAt: String(row?.read_at || "").trim() || null,
      actorId: normalizeId(row?.actor_id) || null,
      actorName: String((payload as any)?.actor_name || "").trim() || "Someone",
      actorAvatarUrl: String((payload as any)?.actor_avatar_url || "").trim() || null,
      entityId: normalizeId(row?.entity_id) || null,
    }
  })
}

export async function markNotificationsRead(userId: string, notificationIds: string[]) {
  const normalizedUserId = normalizeId(userId)
  const ids = Array.from(new Set((notificationIds || []).map((id) => normalizeId(id)).filter(Boolean)))
  if (!normalizedUserId || !ids.length) return

  const res = await supabase
    .from("app_notifications")
    .update({ read_at: new Date().toISOString() })
    .eq("recipient_id", normalizedUserId)
    .in("id", ids)

  if (res.error) throw res.error
}

export function subscribeToUserNotifications(userId: string, onChange: () => void) {
  const normalizedUserId = normalizeId(userId)
  if (!normalizedUserId) return () => void 0
  const channel = supabase.channel(`app-notifications:${normalizedUserId}`)
  channel.on(
    "postgres_changes",
    {
      event: "*",
      schema: "public",
      table: "app_notifications",
      filter: `recipient_id=eq.${normalizedUserId}`,
    },
    () => onChange()
  )
  channel.subscribe()
  return () => {
    void supabase.removeChannel(channel)
  }
}

export function applyCreatorFollowSummary(target: CreatorFollowSummary, next: CreatorFollowSummary) {
  if (target.creatorId !== next.creatorId) return target
  return { ...target, ...next }
}
